import { getGrammarFormPublicationState } from "@/components/slides/publication/grammarFormPublicationState";
import { useSelection } from "@/contexts/SelectionContext";
import { SlideFieldsFragmentDoc } from "@/generated/graphql";
import { useUpdateSlide } from "@/lib/hooks/slides/useUpdateSlide";
import {
  getGrammarFormValidationIssues,
  hasGraphQLErrorCode,
  summarizeGrammarFormValidationIssues,
} from "@/lib/utils/graphqlErrors";
import { ErrorCodes } from "@ai-teach/types";
import { Button, Modal } from "@ai-teach/ui-web";
import { useApolloClient } from "@apollo/client/react";
import { useState } from "react";

interface HideModalProps {
  mode: "hide" | "show" | false;
}

export function HideModal({ mode }: HideModalProps) {
  const { selectedSlideIds, clearAllSelections, toggleHideModal, toggleIsSelectionMode } =
    useSelection();

  const { toggleSlidesIsHidden, publishGrammarFormSlides, toggleLoading, publishLoading } =
    useUpdateSlide();
  const client = useApolloClient();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const isHide = mode === "hide";
  const isBusy = toggleLoading || publishLoading;

  const handleClose = () => {
    setErrorMessage(null);
    toggleHideModal(false);
    toggleIsSelectionMode(true);
  };

  // Translate a mutation failure into a user-facing message. Grammar-form drafts rejected by the
  // publish mutation carry the server's per-slide validation issues — surface those instead of a
  // generic "Failed to update visibility." (mirrors the single-slide CardActionBar path).
  const applyVisibilityError = (error: unknown) => {
    if (hasGraphQLErrorCode(error, ErrorCodes.GRAMMAR_FORM_NOT_PUBLISHABLE)) {
      const validationIssues = getGrammarFormValidationIssues(error);
      setErrorMessage(
        validationIssues
          ? summarizeGrammarFormValidationIssues(validationIssues)
          : "Some slides have content errors and can't be published. Fix them first."
      );
      return;
    }
    if (hasGraphQLErrorCode(error, ErrorCodes.CANNOT_UPDATE_LINKED_COPY)) {
      setErrorMessage("One or more selected slides are linked copies. Unlink them first.");
      return;
    }
    setErrorMessage("Failed to update visibility. Please try again.");
  };

  // Grammar-form DRAFTS can't be "shown": the visibility mutation rejects them server-side. On a
  // bulk Show, route each selected slide by its publication state — ready drafts get PUBLISHED,
  // everything else uses the normal Show, and drafts blocked by content errors are called out so
  // the admin fixes them first (they can't be published in their current state).
  //
  // Returns `true` when the action was blocked (content errors) and the caller should stop.
  const handleShow = async (): Promise<boolean> => {
    const publishIds: string[] = [];
    const showIds: string[] = [];
    let blockedCount = 0;

    for (const id of selectedSlideIds) {
      const slide = client.cache.readFragment({
        id: `Slide:${id}`,
        fragment: SlideFieldsFragmentDoc,
      });

      // Not in cache (or a non-grammar slide) → nothing special, just show it.
      if (!slide) {
        showIds.push(id);
        continue;
      }

      const state = getGrammarFormPublicationState(slide);
      // Linked copies inherit publication from their original and can't be published directly, so
      // they keep the normal Show path (mirrors CardActionBar).
      if (!slide.isLinkedCopy && state === "ready_draft") {
        publishIds.push(id);
      } else if (!slide.isLinkedCopy && state === "blocked_draft") {
        blockedCount += 1;
      } else {
        showIds.push(id);
      }
    }

    if (blockedCount > 0) {
      const noun = blockedCount === 1 ? "slide has" : "slides have";
      setErrorMessage(
        `${blockedCount} ${noun} content errors and can't be published — fix them first.`
      );
      return true;
    }

    if (publishIds.length > 0) await publishGrammarFormSlides(publishIds);
    if (showIds.length > 0) await toggleSlidesIsHidden(showIds, false);
    return false;
  };

  const handleConfirm = async () => {
    try {
      setErrorMessage(null);
      if (isHide) {
        // Hiding is valid for every slide kind — no publication routing needed.
        await toggleSlidesIsHidden(selectedSlideIds, true);
      } else {
        const blocked = await handleShow();
        // handleShow sets its own error message and returns early when it blocks the action.
        if (blocked) return;
      }
      clearAllSelections();
      toggleHideModal(false);
    } catch (error) {
      applyVisibilityError(error);
    }
  };

  if (!mode) return null;

  return (
    <Modal
      isOpen={!!mode}
      onClose={handleClose}
      title={isHide ? "Hide slides" : "Show slides"}
      size="sm"
    >
      <div className="flex flex-col">
        {errorMessage ? (
          <>
            <p className="text-secondary-200 mb-6 text-center">{errorMessage}</p>
            <div className="flex justify-center">
              <Button onClick={handleClose} variant="secondary">
                Close
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-secondary-200 mb-6 text-center">
              {isHide
                ? "The selected slides will be hidden from students. You can show them again anytime."
                : "The selected slides will be visible to students again."}
            </p>
            <div className="flex gap-3 justify-between">
              <Button onClick={handleClose} variant="secondary">
                Cancel
              </Button>
              <Button onClick={handleConfirm} variant="default" disabled={isBusy}>
                {isHide ? "Hide" : "Show"}
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
